import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { COLORS } from '../constants/theme';
import { CustomReportColleague, CustomReportDetail } from '../services/apiService';

const { width } = Dimensions.get('window');

export interface ReportProductDraft {
  productId: number;
  name: string;
  isin: string;
  ccy: string;
  allocatedNotional: string;
  maxNotional: number;
  selected: boolean;
}

export interface CreateReportSubmitInput {
  name: string;
  memberIds: string[];
  products: Array<{ productId: number; allocatedNotional: number }>;
}

interface CreateReportModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  colleagues: CustomReportColleague[];
  initialReport?: CustomReportDetail | null;
  initialProducts: ReportProductDraft[];
  loadingColleagues?: boolean;
  loadingProducts?: boolean;
  submitting?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (input: CreateReportSubmitInput) => void;
  onDelete?: () => void;
}

const parseNotional = (value: string): number => {
  return Number(String(value).replace(/,/g, '').trim());
};

const formatMaxNotional = (value: number): string => {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export const CreateReportModal: React.FC<CreateReportModalProps> = ({
  visible,
  mode,
  colleagues,
  initialReport,
  initialProducts,
  loadingColleagues = false,
  loadingProducts = false,
  submitting = false,
  errorMessage,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const isTabletOrDesktop = width >= 768;
  const slideAnim = useRef(new Animated.Value(width)).current;
  const [name, setName] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [products, setProducts] = useState<ReportProductDraft[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setName(initialReport?.name || '');
    setSelectedMemberIds((initialReport?.members || []).map(member => member.id));
    setProductSearch('');
    setLocalError(null);
  }, [visible, initialReport]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setProducts(initialProducts);
  }, [visible, initialProducts]);

  useEffect(() => {
    if (visible) {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 11,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: width,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, slideAnim]);

  const toggleMember = (id: string) => {
    setSelectedMemberIds(prev =>
      prev.includes(id) ? prev.filter(memberId => memberId !== id) : [...prev, id]
    );
  };

  const toggleProduct = (productId: number) => {
    setProducts(prev =>
      prev.map(product =>
        product.productId === productId ? { ...product, selected: !product.selected } : product
      )
    );
  };

  const updateAllocatedNotional = (productId: number, value: string) => {
    setProducts(prev =>
      prev.map(product =>
        product.productId === productId ? { ...product, allocatedNotional: value } : product
      )
    );
  };

  const clampAllocatedNotional = (productId: number) => {
    setProducts(prev =>
      prev.map(product => {
        if (product.productId !== productId) {
          return product;
        }
        const parsed = parseNotional(product.allocatedNotional);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          return { ...product, allocatedNotional: String(Math.round(product.maxNotional)) };
        }
        const next = Math.min(parsed, product.maxNotional);
        return { ...product, allocatedNotional: String(Math.round(next)) };
      })
    );
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setLocalError('Portfolio name is required.');
      return;
    }

    const selectedProducts = products.filter(product => product.selected);
    if (selectedProducts.length === 0) {
      setLocalError('Select at least one product.');
      return;
    }

    const parsedProducts: Array<{ productId: number; allocatedNotional: number }> = [];
    for (const product of selectedProducts) {
      const allocatedNotional = parseNotional(product.allocatedNotional);
      if (!Number.isFinite(allocatedNotional) || allocatedNotional <= 0) {
        setLocalError(`Enter a valid notional for ${product.isin || product.name}.`);
        return;
      }
      if (allocatedNotional > product.maxNotional) {
        setLocalError(
          `Notional for ${product.isin || product.name} cannot exceed ${formatMaxNotional(product.maxNotional)}.`
        );
        return;
      }
      parsedProducts.push({
        productId: product.productId,
        allocatedNotional,
      });
    }

    setLocalError(null);
    onSubmit({
      name: name.trim(),
      memberIds: selectedMemberIds,
      products: parsedProducts,
    });
  };

  const selectedCount = products.filter(product => product.selected).length;
  const canSubmit = name.trim().length > 0 && selectedCount > 0 && !submitting && !loadingProducts;
  const displayError = localError || errorMessage;
  const query = productSearch.trim().toLowerCase();
  const visibleProducts = query
    ? products.filter(product => {
        const nameValue = (product.name || '').toLowerCase();
        const isin = (product.isin || '').toLowerCase();
        return nameValue.includes(query) || isin.includes(query);
      })
    : products;

  return (
    <Modal visible={visible} animationType='none' transparent={true} onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={onClose} />
        <Animated.View
          style={[
            styles.modalContent,
            isTabletOrDesktop && styles.modalContentDesktop,
            { transform: [{ translateX: slideAnim }] },
          ]}
        >
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {mode === 'edit' ? 'Edit portfolio' : 'Create portfolio'}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.filtersScrollView}
            contentContainerStyle={styles.filtersContent}
            showsVerticalScrollIndicator={false}
          >
            <Text style={styles.helperText}>
              Select the products from your portfolio and set the notional for each one.
            </Text>

            <Text style={styles.fieldLabel}>Portfolio Name</Text>
            <TextInput
              style={styles.nameInput}
              placeholder='e.g. Client A book'
              placeholderTextColor='#9CA3AF'
              value={name}
              onChangeText={setName}
              maxLength={100}
            />

            <Text style={styles.fieldLabel}>Visible to colleagues</Text>
            {loadingColleagues ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size='small' color={COLORS.primary} />
              </View>
            ) : colleagues.length === 0 ? (
              <Text style={styles.emptyText}>No colleagues from your company are available.</Text>
            ) : (
              colleagues.map(colleague => {
                const selected = selectedMemberIds.includes(colleague.id);
                return (
                  <TouchableOpacity
                    key={colleague.id}
                    style={styles.optionRow}
                    onPress={() => toggleMember(colleague.id)}
                    activeOpacity={0.7}
                  >
                    <View>
                      <Text style={styles.optionLabel}>{colleague.name || colleague.email}</Text>
                      <Text style={styles.optionEmail}>{colleague.email}</Text>
                    </View>
                    <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                      {selected && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                  </TouchableOpacity>
                );
              })
            )}

            <Text style={styles.fieldLabel}>
              Products{selectedCount > 0 ? ` (${selectedCount} selected)` : ''}
            </Text>
            <TextInput
              style={styles.searchInput}
              placeholder='Search by name or ISIN'
              placeholderTextColor='#9CA3AF'
              value={productSearch}
              onChangeText={setProductSearch}
            />
            {loadingProducts ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size='small' color={COLORS.primary} />
                <Text style={styles.emptyText}>Loading products...</Text>
              </View>
            ) : products.length === 0 ? (
              <Text style={styles.emptyText}>There are no products assigned to your portfolio.</Text>
            ) : visibleProducts.length === 0 ? (
              <Text style={styles.emptyText}>No products match the search.</Text>
            ) : (
              visibleProducts.map(product => (
                <View key={product.productId} style={styles.productRow}>
                  <TouchableOpacity
                    style={styles.productSelect}
                    onPress={() => toggleProduct(product.productId)}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.checkbox, product.selected && styles.checkboxSelected]}>
                      {product.selected && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <View style={styles.productInfo}>
                      <Text style={styles.productName} numberOfLines={2}>
                        {product.name}
                      </Text>
                      <Text style={styles.productMeta}>
                        {product.isin}
                        {product.ccy ? ` · ${product.ccy}` : ''}
                      </Text>
                      <Text style={styles.productMeta}>
                        Max {formatMaxNotional(product.maxNotional)}
                      </Text>
                    </View>
                  </TouchableOpacity>
                  <TextInput
                    style={[
                      styles.notionalInput,
                      !product.selected && styles.notionalInputDisabled,
                    ]}
                    value={product.allocatedNotional}
                    keyboardType='numeric'
                    editable={product.selected}
                    onChangeText={value => updateAllocatedNotional(product.productId, value)}
                    onBlur={() => clampAllocatedNotional(product.productId)}
                  />
                </View>
              ))
            )}

            {displayError ? <Text style={styles.errorText}>{displayError}</Text> : null}
          </ScrollView>

          <View style={styles.modalFooter}>
            {mode === 'edit' && onDelete && (
              <TouchableOpacity style={styles.deleteButton} onPress={onDelete} disabled={submitting}>
                <Text style={styles.deleteButtonText}>Delete portfolio</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.applyButton, !canSubmit && styles.applyButtonDisabled]}
              onPress={handleSubmit}
              disabled={!canSubmit}
            >
              <Text style={styles.applyButtonText}>
                {submitting ? 'Saving...' : mode === 'edit' ? 'Save changes' : 'Create portfolio'}
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: width >= 768 ? 480 : width * 0.9,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: -2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 10,
  },
  modalContentDesktop: {
    width: 480,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    fontSize: 20,
    color: '#6B7280',
    fontWeight: '600',
  },
  filtersScrollView: {
    flex: 1,
  },
  filtersContent: {
    padding: 16,
  },
  helperText: {
    fontSize: 13,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 16,
    lineHeight: 18,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
    marginBottom: 8,
    marginTop: 8,
  },
  nameInput: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1F2937',
    marginBottom: 16,
  },
  searchInput: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 13,
    color: '#1F2937',
    marginBottom: 8,
  },
  loadingContainer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#9CA3AF',
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 12,
  },
  optionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  optionLabel: {
    fontSize: 14,
    color: '#1F2937',
    fontFamily: 'Montserrat_500Medium',
  },
  optionEmail: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
  },
  productRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    gap: 12,
  },
  productSelect: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  productInfo: {
    flex: 1,
    paddingRight: 8,
  },
  productName: {
    fontSize: 13,
    color: '#1F2937',
    fontFamily: 'Montserrat_500Medium',
  },
  productMeta: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  notionalInput: {
    width: 110,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
    color: '#1F2937',
    textAlign: 'right',
    backgroundColor: '#FFFFFF',
  },
  notionalInputDisabled: {
    backgroundColor: '#F9FAFB',
    color: '#9CA3AF',
  },
  errorText: {
    marginTop: 12,
    fontSize: 13,
    color: COLORS.error,
  },
  modalFooter: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    gap: 10,
  },
  applyButton: {
    backgroundColor: COLORS.primary || '#3B82F6',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  applyButtonDisabled: {
    opacity: 0.5,
  },
  applyButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    fontFamily: 'Montserrat_600SemiBold',
  },
  deleteButton: {
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.error,
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.error,
    fontFamily: 'Montserrat_600SemiBold',
  },
});
